import { useState, useEffect } from 'react';
import { db } from '@/lib/firebase';
import {
    doc,
    onSnapshot,
    collection,
    addDoc,
    serverTimestamp,
    query,
    where,
    getDocs,
    updateDoc,
    orderBy,
    getDoc
} from 'firebase/firestore';

export interface KYCStatus {
    level: number;
    phone: 'Completed' | 'Pending' | 'Action Required' | 'Locked' | 'Rejected';
    account: 'Completed' | 'Pending' | 'Action Required' | 'Locked' | 'Rejected';
    idcard: 'Completed' | 'Pending' | 'Action Required' | 'Locked' | 'Rejected';
    selfie: 'Completed' | 'Pending' | 'Action Required' | 'Locked' | 'Rejected';
    creditCard: 'Completed' | 'Pending' | 'Action Required' | 'Locked' | 'Rejected';
    address: 'Completed' | 'Pending' | 'Action Required' | 'Locked' | 'Rejected';
    business: 'Completed' | 'Pending' | 'Action Required' | 'Locked' | 'Rejected';
}

export const useKYC = (userId?: string) => {
    const [status, setStatus] = useState<KYCStatus | null>(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        if (!userId) {
            setLoading(false);
            return;
        }
        const unsubscribe = onSnapshot(doc(db, 'users', userId), (docSnap) => {
            if (docSnap.exists()) {
                const data = docSnap.data();
                const kycData = data.kyc || {
                    level: 0,
                    phone: 'Action Required',
                    account: 'Action Required',
                    idcard: 'Action Required',
                    selfie: 'Action Required',
                    creditCard: 'Action Required',
                    address: 'Action Required',
                    business: 'Action Required'
                };

                // FORCE UNLOCK: Convert any 'Locked' status from DB to 'Action Required'
                // This ensures existing users with 'Locked' status can proceed immediately
                if (kycData.phone === 'Locked') kycData.phone = 'Action Required';
                if (kycData.account === 'Locked') kycData.account = 'Action Required';
                if (kycData.idcard === 'Locked') kycData.idcard = 'Action Required';
                if (kycData.selfie === 'Locked') kycData.selfie = 'Action Required';
                if (kycData.creditCard === 'Locked') kycData.creditCard = 'Action Required';
                if (kycData.address === 'Locked') kycData.address = 'Action Required';
                if (kycData.business === 'Locked') kycData.business = 'Action Required';

                // Auto-fix Level Logic: Recalculate level based on completed items
                // Normalize field access (handle idcard vs ID_CARD, phone vs PHONE, etc)
                const isComp = (f: string): boolean => {
                    const status = kycData[f] || kycData[f.toUpperCase()] || (f === 'idcard' ? kycData['ID_CARD'] : undefined);
                    return status === 'Completed' || status === 'completed' || status === 'Approved' || status === 'APPROVED';
                };

                let calculatedLevel = 0;
                if (isComp('phone') && isComp('account')) {
                    calculatedLevel = 1;
                    if (isComp('idcard') && isComp('selfie')) {
                        calculatedLevel = 2;
                        if (isComp('address') && isComp('business') && isComp('creditCard')) {
                            calculatedLevel = 3;
                        }
                    }
                }

                // Allow manual level if it's higher than calculated, but otherwise sync it
                kycData.level = Math.max(calculatedLevel, kycData.level || 0);

                setStatus(kycData);
            } else {
                // Initialize default KYC status if user doc doesn't exist
                setStatus({
                    level: 0,
                    phone: 'Action Required',
                    account: 'Action Required',
                    idcard: 'Action Required',
                    selfie: 'Action Required',
                    creditCard: 'Action Required',
                    address: 'Action Required',
                    business: 'Action Required'
                });
            }
            setLoading(false);
        });

        return () => unsubscribe();
    }, [userId]);

    const submitApplication = async (type: string, data: any) => {
        if (!userId) throw new Error("User not authenticated");
        try {
            // Fetch current user's name to store with application
            let userName = userId;
            try {
                const userDoc = await getDoc(doc(db, 'users', userId));
                if (userDoc.exists()) {
                    const userData = userDoc.data();
                    userName = userData.name || userData.userName || userData.displayName || userData.fullName || userData.email || userId;
                }
            } catch (e) {
                console.error('Error fetching user name for application:', e);
            }

            // 1. Add application to global collection for admin
            await addDoc(collection(db, 'applications'), {
                userId,
                userName,
                type, // 'ID_CARD', 'ACCOUNT', etc
                data,
                status: 'PENDING',
                createdAt: serverTimestamp()
            });

            // 2. Update status in user doc
            const fieldMap: Record<string, string> = {
                'PHONE': 'phone',
                'ACCOUNT': 'account',
                'ID_CARD': 'idcard',
                'SELFIE': 'selfie',
                'CREDIT_CARD': 'creditCard',
                'ADDRESS': 'address',
                'BUSINESS': 'business'
            };

            const field = fieldMap[type];
            if (field && status) {
                const newKyc = { ...status, [field]: 'Pending' };
                await updateDoc(doc(db, 'users', userId), {
                    kyc: newKyc
                });
            }

            return { success: true };
        } catch (error) {
            console.error("KYC Submission error:", error);
            return { success: false, error };
        }
    };

    // Admin Functions
    const getAllApplications = async () => {
        const q = query(
            collection(db, 'applications'),
            orderBy('createdAt', 'desc')
        );
        const snapshot = await getDocs(q);

        // Fetch user names for each application
        const applicationsWithNames = await Promise.all(
            snapshot.docs.map(async (d) => {
                const appData = d.data();
                // Use stored userName first, then fallback to fetching from users collection
                let userName = appData.userName || appData.userId;

                // If userName looks like a userId (no spaces, long string), try to fetch from users collection
                if (!appData.userName || appData.userName === appData.userId) {
                    try {
                        const userDoc = await getDoc(doc(db, 'users', appData.userId));
                        if (userDoc.exists()) {
                            const userData = userDoc.data();
                            userName = userData.name || userData.userName || userData.displayName || userData.fullName || userData.email || appData.userId;
                        }
                    } catch (error) {
                        console.error('Error fetching user name:', error);
                    }
                }

                return { id: d.id, ...appData, userName };
            })
        );

        return applicationsWithNames;
    };

    const processApplication = async (appId: string, userId: string, type: string, newStatus: 'Approved' | 'Rejected', rejectionReason?: string) => {
        try {
            // 1. Update Application Status
            await updateDoc(doc(db, 'applications', appId), {
                status: newStatus,
                processedAt: serverTimestamp(),
                ...(rejectionReason ? { rejectionReason } : {})
            });

            // 2. Update User KYC Status
            const fieldMap: Record<string, string> = {
                'PHONE': 'phone',
                'ACCOUNT': 'account',
                'ID_CARD': 'idcard',
                'SELFIE': 'selfie',
                'CREDIT_CARD': 'creditCard',
                'ADDRESS': 'address',
                'BUSINESS': 'business'
            };

            const field = fieldMap[type];
            if (!field) return { success: false, error: 'Invalid type' };

            const userRef = doc(db, 'users', userId);
            const userSnap = await getDoc(userRef);

            if (userSnap.exists()) {
                const userData = userSnap.data();
                const kycData = userData.kyc || {};
                const kycStatus = newStatus === 'Approved' ? 'Completed' : 'Rejected';
                const nextKYC = { ...kycData, [field]: kycStatus };
                let newLevel = kycData.level || 0;

                let updates: any = {
                    [`kyc.${field}`]: kycStatus,
                    ...(rejectionReason ? { [`kyc_rejection.${field}`]: rejectionReason } : {})
                };

                // Level Up Logic based on NEXT state
                // Level 1: Phone & Account
                if (nextKYC.phone === 'Completed' && nextKYC.account === 'Completed') {
                    newLevel = Math.max(newLevel, 1);
                }

                // Level 2: ID Card & Selfie
                if (nextKYC.idcard === 'Completed' && nextKYC.selfie === 'Completed') {
                    newLevel = Math.max(newLevel, 2);
                }

                // Level 3: Address & Business & CreditCard
                if (nextKYC.address === 'Completed' && nextKYC.business === 'Completed' && nextKYC.creditCard === 'Completed') {
                    newLevel = Math.max(newLevel, 3);
                }

                if (newLevel !== (kycData.level || 0)) {
                    updates['kyc.level'] = newLevel;
                }

                await updateDoc(userRef, updates);
            }

            return { success: true };
        } catch (error) {
            console.error("Process Application Error:", error);
            return { success: false, error };
        }
    };

    return { status, loading, submitApplication, getAllApplications, processApplication };
};
